import React, { useState, useRef } from 'react';
import {
    StyleSheet,
    View,
    Text,
    TouchableOpacity,
    Image,
    Dimensions,
    FlatList,
    SafeAreaView,
    Platform
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import Animated, {
    useSharedValue,
    useAnimatedStyle,
    interpolate,
    useAnimatedScrollHandler,
    withRepeat,
    withSequence,
    withTiming,
    withSpring
} from 'react-native-reanimated';
import AsyncStorage from '@react-native-async-storage/async-storage';

const { width, height } = Dimensions.get('window');

const DATA = [
    {
        id: '1',
        title: 'Inspire. Educate. Connect.',
        description: 'The all-in-one platform for youth empowerment and community growth.',
        icon: 'sparkles-outline',
        gradient: ['#10B981', '#059669'], // Greenish first
        tagline: 'Welcome Home',
        bgColor: '#D1FAE5',
    },
    {
        id: '2',
        title: 'Soma & Pesa',
        description: 'Master your finances and build vocational skills for a brighter future.',
        icon: 'wallet-outline',
        gradient: ['#EC4899', '#DB2777'], // Pink second
        tagline: 'Learn & Earn',
        bgColor: '#FCE7F3',
    },
    {
        id: '3',
        title: 'Community & Faith',
        description: 'Daily verses and mentor connections to grow your spirit and network.',
        icon: 'people-outline',
        gradient: ['#F59E0B', '#D97706'], // Amber remains third
        tagline: 'Grow Together',
        bgColor: '#FEF3C7',
    },
    {
        id: '4',
        title: 'Ready to Thrive?',
        description: 'Join 5,000+ young people building their tomorrow. Your time is now!',
        icon: 'rocket-outline',
        gradient: ['#6366F1', '#4F46E5'], // Indigo last
        tagline: 'Get Started',
        bgColor: '#E0E7FF',
    },
];

const OnboardingItem = ({ item, index, x }) => {
    const floatValue = useSharedValue(0);

    React.useEffect(() => {
        floatValue.value = withRepeat(
            withSequence(
                withTiming(-15, { duration: 2000 }),
                withTiming(0, { duration: 2000 })
            ),
            -1,
            true
        );
    }, []);

    const animatedIconStyle = useAnimatedStyle(() => {
        const scale = interpolate(
            x.value,
            [(index - 1) * width, index * width, (index + 1) * width],
            [0.4, 1, 0.4]
        );
        const opacity = interpolate(
            x.value,
            [(index - 1) * width, index * width, (index + 1) * width],
            [0, 1, 0]
        );
        return {
            transform: [
                { scale },
                { translateY: floatValue.value },
                { rotate: `${interpolate(x.value, [(index - 1) * width, index * width, (index + 1) * width], [-10, 0, 10])}deg` }
            ],
            opacity,
        };
    });

    const logoStyle = useAnimatedStyle(() => {
        const opacity = interpolate(
            x.value,
            [(index - 1) * width, index * width, (index + 1) * width],
            [0, 1, 0]
        );
        return { opacity };
    });

    return (
        <View style={styles.itemContainer}>
            <Animated.View style={[styles.logoTopContainer, logoStyle]}>
                <Image
                    source={require('../../assets/splash.png')}
                    style={styles.smallLogo}
                    resizeMode="contain"
                />
            </Animated.View>

            <View style={styles.visualContainer}>
                <Animated.View style={[styles.iconWrapper, animatedIconStyle]}>
                    <View style={[styles.iconBlob, { backgroundColor: item.gradient[0] + '20' }]} />
                    <Ionicons name={item.icon} size={width * 0.28} color={item.gradient[0]} />
                </Animated.View>
            </View>

            <View style={styles.textContainer}>
                <View style={styles.taglineWrapper}>
                    <Text style={[styles.tagline, { color: item.gradient[0] }]}>{item.tagline}</Text>
                </View>
                <Text style={styles.title}>{item.title}</Text>
                <Text style={styles.description}>{item.description}</Text>
            </View>
        </View>
    );
};

const OnboardingScreen = ({ navigation }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const flatListRef = useRef(null);
    const x = useSharedValue(0);

    const onScroll = useAnimatedScrollHandler({
        onScroll: (event) => {
            x.value = event.contentOffset.x;
        },
    });

    const onViewableItemsChanged = useRef(({ viewableItems }) => {
        if (viewableItems[0]) {
            setCurrentIndex(viewableItems[0].index);
        }
    }).current;

    const handleNext = async () => {
        if (currentIndex < DATA.length - 1) {
            flatListRef.current.scrollToIndex({ index: currentIndex + 1 });
        } else {
            await AsyncStorage.setItem('hasSeenOnboarding', 'true');
            navigation.replace('Login');
        }
    };

    const handleSkip = async () => {
        await AsyncStorage.setItem('hasSeenOnboarding', 'true');
        navigation.replace('Login');
    };

    return (
        <Animated.View style={[styles.container, { backgroundColor: DATA[currentIndex].bgColor }]}>
            <StatusBar style="dark" />

            <View style={styles.backgroundDecor}>
                <View style={[styles.decorCircle, { top: -width * 0.4, right: -width * 0.2, backgroundColor: DATA[currentIndex].gradient[0] + '08' }]} />
                <View style={[styles.decorCircle, { bottom: -width * 0.3, left: -width * 0.3, backgroundColor: DATA[currentIndex].gradient[1] + '08' }]} />
                <View style={[styles.softGradient, { backgroundColor: DATA[currentIndex].gradient[0] + '10' }]} />
            </View>

            <SafeAreaView style={{ flex: 1 }}>
                <View style={styles.header}>
                    <TouchableOpacity onPress={handleSkip} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                        <Text style={styles.skipText}>Skip</Text>
                    </TouchableOpacity>
                </View>

                <Animated.FlatList
                    ref={flatListRef}
                    data={DATA}
                    renderItem={({ item, index }) => <OnboardingItem item={item} index={index} x={x} />}
                    keyExtractor={(item) => item.id}
                    horizontal
                    pagingEnabled
                    showsHorizontalScrollIndicator={false}
                    onScroll={onScroll}
                    scrollEventThrottle={16}
                    onViewableItemsChanged={onViewableItemsChanged}
                    viewabilityConfig={{ viewAreaCoveragePercentThreshold: 50 }}
                />

                <View style={styles.footer}>
                    <View style={styles.pagination}>
                        {DATA.map((_, index) => {
                            const dotStyle = useAnimatedStyle(() => {
                                const dotWidth = interpolate(
                                    x.value,
                                    [(index - 1) * width, index * width, (index + 1) * width],
                                    [6, 20, 6]
                                );
                                const opacity = interpolate(
                                    x.value,
                                    [(index - 1) * width, index * width, (index + 1) * width],
                                    [0.2, 1, 0.2]
                                );
                                return {
                                    width: dotWidth,
                                    opacity,
                                    backgroundColor: DATA[currentIndex].gradient[0],
                                };
                            });
                            return (
                                <Animated.View key={index.toString()} style={[styles.dot, dotStyle]} />
                            );
                        })}
                    </View>

                    <TouchableOpacity
                        style={styles.nextButton}
                        onPress={handleNext}
                        activeOpacity={0.8}
                    >
                        <LinearGradient
                            colors={DATA[currentIndex].gradient}
                            style={styles.buttonGradient}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 0 }}
                        >
                            <Text style={styles.buttonText}>
                                {currentIndex === DATA.length - 1 ? 'Start Thriving' : 'Continue'}
                            </Text>
                            <Ionicons name="chevron-forward" size={18} color="#FFF" />
                        </LinearGradient>
                    </TouchableOpacity>
                </View>
            </SafeAreaView>
        </Animated.View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    backgroundDecor: {
        ...StyleSheet.absoluteFillObject,
        overflow: 'hidden',
    },
    decorCircle: {
        position: 'absolute',
        width: width * 1.2,
        height: width * 1.2,
        borderRadius: width * 0.6,
    },
    softGradient: {
        position: 'absolute',
        width: width,
        height: height,
        opacity: 0.5,
    },
    header: {
        paddingHorizontal: 25,
        paddingTop: Platform.OS === 'ios' ? 40 : 50,
        alignItems: 'flex-end',
        zIndex: 10,
    },
    skipText: {
        fontSize: 13,
        color: '#64748B',
        fontWeight: '700',
        letterSpacing: 0.5,
    },
    itemContainer: {
        width: width,
        alignItems: 'center',
        paddingHorizontal: 30,
        paddingTop: height * 0.02,
    },
    logoTopContainer: {
        marginBottom: height * 0.02,
        alignItems: 'center',
    },
    smallLogo: {
        width: 180,
        height: 100,
        opacity: 0.9,
    },
    visualContainer: {
        height: height * 0.3,
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 10,
    },
    iconWrapper: {
        justifyContent: 'center',
        alignItems: 'center',
    },
    iconBlob: {
        position: 'absolute',
        width: width * 0.35,
        height: width * 0.35,
        borderRadius: width * 0.175,
        zIndex: -1,
    },
    textContainer: {
        alignItems: 'center',
    },
    taglineWrapper: {
        paddingHorizontal: 14,
        paddingVertical: 4,
        borderRadius: 20,
        backgroundColor: 'rgba(255,255,255,0.7)',
        marginBottom: 12,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.8)',
    },
    tagline: {
        fontSize: 10,
        fontWeight: '900',
        letterSpacing: 2,
        textTransform: 'uppercase',
    },
    title: {
        fontSize: 22,
        fontWeight: '900',
        color: '#0F172A',
        textAlign: 'center',
        marginBottom: 10,
        letterSpacing: -0.8,
        lineHeight: 28,
    },
    description: {
        fontSize: 14,
        color: '#475569',
        textAlign: 'center',
        lineHeight: 22,
        paddingHorizontal: 15,
    },
    footer: {
        paddingHorizontal: 30,
        paddingBottom: Platform.OS === 'ios' ? 25 : 40,
    },
    pagination: {
        flexDirection: 'row',
        justifyContent: 'center',
        marginBottom: 25,
    },
    dot: {
        height: 5,
        borderRadius: 2.5,
        marginHorizontal: 3,
    },
    nextButton: {
        borderRadius: 12,
        overflow: 'hidden',
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.12,
        shadowRadius: 12,
        elevation: 6,
    },
    buttonGradient: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 14,
        paddingHorizontal: 22,
    },
    buttonText: {
        color: '#FFFFFF',
        fontSize: 16,
        fontWeight: '800',
        marginRight: 6,
        letterSpacing: -0.3,
    },
});

export default OnboardingScreen;
